import * as React from 'react';

import { useApiRequestControllable } from '@europrocurement/l2d-modules';
import { useAchatsService } from '@europrocurement/l2d-domain';

/**
 * Structure des données du contenu d'un mail depuis l'API
 */
export type MailContent = {
    subject: string;
    body: string;
    recipient: {
        email: string;
        name?: string;
    };
    status: string;
    reminderPublisherId: string;
    isReminder: boolean;
};

/**
 * Options pour le hook useMailContent
 */
type UseMailContentOptions = {
    /**
     * Tableau de dépendances qui déclencheront un rafraîchissement du contenu
     * lorsqu'elles changent
     */
    triggerDependencies?: React.DependencyList;

    /**
     * Si true, désactive le chargement automatique au montage du composant
     */
    disableAutoFetch?: boolean;

    /**
     * Délai d'attente avant d'exécuter la requête en millisecondes
     */
    requestDelay?: number;
};

/**
 * Hook pour récupérer et gérer le contenu d'un mail pour un rappel fournisseur
 *
 * @param reminderId - ID du rappel fournisseur pour lequel récupérer le contenu du mail
 * @param options - Options de configuration du hook
 * @returns Objet contenant les données du contenu du mail et l'état de chargement
 */
export function useMailContent(reminderId?: string, options: UseMailContentOptions = {}) {
    const { triggerDependencies = [], disableAutoFetch = false, requestDelay = 4000 } = options;

    const { reminderPublisherApi } = useAchatsService();

    const [mailContent, setMailContent] = React.useState<MailContent | null>(null);
    const [errorOccurred, setErrorOccurred] = React.useState(false);
    const [isTimeoutActive, setIsTimeoutActive] = React.useState(false);
    const hasInitializedRef = React.useRef(false);
    const timeoutRef = React.useRef<number | null>(null);

    const { request, requestState } = useApiRequestControllable();

    /**
     * Récupère le contenu du mail depuis l'API avec délai
     */
    const fetchMailContent = React.useCallback(
        async (id: string) => {
            if (!id || requestState.isProcessing || isTimeoutActive) {
                return;
            }

            // Démarrer le timeout
            setIsTimeoutActive(true);

            timeoutRef.current = setTimeout(() => {
                // Le timeout est terminé, mais on reste en loading pendant la requête
                setIsTimeoutActive(false);

                request(() => reminderPublisherApi.getMailContentReminderPublisherItem({ id }), {
                    withToaster: false,
                    successCallback: (response) => {
                        setMailContent(response.data);
                        setErrorOccurred(false);
                    },
                    failureCallback: () => {
                        setErrorOccurred(true);
                    },
                });
            }, requestDelay);
        },
        [requestState.isProcessing, isTimeoutActive, requestDelay, request, reminderPublisherApi],
    );

    /**
     * Fonction pour déclencher manuellement la récupération du contenu du mail
     */
    const refreshMailContent = React.useCallback(() => {
        // Nettoyer le timeout précédent si il existe
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setIsTimeoutActive(false);

        if (!reminderId) return;
        fetchMailContent(reminderId);
    }, [fetchMailContent, reminderId]);

    /**
     * Effet pour initialiser le contenu du mail au montage du composant
     * ou lorsque l'ID du rappel fournisseur change
     */
    React.useEffect(() => {
        if (disableAutoFetch) {
            return;
        }

        if (
            reminderId &&
            !hasInitializedRef.current &&
            !requestState.isProcessing &&
            !isTimeoutActive
        ) {
            hasInitializedRef.current = true;
            fetchMailContent(reminderId);
        }
    }, [
        fetchMailContent,
        reminderId,
        requestState.isProcessing,
        disableAutoFetch,
        isTimeoutActive,
    ]);

    /**
     * Effet pour rafraîchir le contenu du mail lorsque les dépendances de déclenchement changent
     */
    React.useEffect(() => {
        // Ne pas exécuter lors du premier rendu ou si l'effet n'a pas été initialisé
        if (!hasInitializedRef.current) {
            return;
        }

        // Éviter les appels récursifs en vérifiant si une requête est déjà en cours
        if (reminderId && !requestState.isProcessing && !isTimeoutActive) {
            // Nettoyer le timeout précédent si il existe
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            fetchMailContent(reminderId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...triggerDependencies]);

    /**
     * Cleanup du timeout au démontage du composant
     */
    React.useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    /**
     * Le loading state est actif si :
     * 1. Le timeout est en cours (isTimeoutActive)
     * 2. OU la requête API est en cours (requestState.isProcessing)
     */
    const isLoading = isTimeoutActive || requestState.isProcessing;

    return {
        mailContent,
        refresh: refreshMailContent,
        isLoading,
        error: requestState.error,
        isSuccess: requestState.success,
        errorOccurred,
        isTimeoutActive, // Exposé au cas où vous en auriez besoin
    };
}

export default useMailContent;
